import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback, getViewParameterValue } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));

    // TK: pre-select capture-phase handler for variant cascading.
    // Lives in this asset (not inline snippet) so it's cache-resilient via
    // Shopify's content-hashed asset URLs. Without it, the inline script
    // version can lag behind theme deploys due to page_cache.
    this.#setupPreSelectCapture();
  }

  /** @type {boolean} */
  #tkFixingFlag = false;
  /** @type {any} */
  #tkProduct = null;

  /**
   * TK: PRE-SELECT v capture fázi — běží PŘED `variantChanged`.
   * Pokrývá obě cesty: radio (Barva/Balení) i SELECT (Model zařízení).
   * Po user změně option N: najde nejlépe pasující variantu (prefer available,
   * fallback first existing) a pre-emptivně updatne radio/select options N+1..K
   * v DOMu. Shopify pak fetchne s validní option_values kombinací místo
   * stale hodnot z předchozí varianty → server vrátí kompletní variant data
   * (SKU/EAN/cena), nikoliv blank z `selected_variant=nil` fallbacku.
   */
  #setupPreSelectCapture() {
    if (this.dataset.templateProductMatch !== 'true') return;
    const productUrl = this.dataset.productUrl;
    if (!productUrl) return;

    fetch(productUrl + '.js', { credentials: 'same-origin' })
      .then((r) => r.json())
      .then((p) => {
        this.#tkProduct = p;
      })
      .catch(() => {});

    this.addEventListener(
      'change',
      (e) => {
        if (!this.#tkProduct || this.#tkFixingFlag) return;
        const target = /** @type {EventTarget|null} */ (e.target);
        let changedOptionName = null;
        let changedValue = null;

        if (target instanceof HTMLInputElement && target.type === 'radio') {
          const fs = target.closest('fieldset');
          if (!fs) return;
          changedOptionName = fs.getAttribute('data-option-name');
          changedValue = target.value;
        } else if (target instanceof HTMLSelectElement) {
          const m = (target.name || '').match(/options\[(.+)\]/);
          if (!m) return;
          changedOptionName = m[1];
          changedValue = target.value;
        } else {
          return;
        }
        if (!changedOptionName || !this.#tkProduct.options) return;

        // product.options z product.js: pole stringů (legacy) NEBO objekty {name, position, values}
        const optionNames = this.#tkProduct.options.map(
          (/** @type {any} */ o) => (typeof o === 'string' ? o : (o && o.name) || '')
        );
        const changedIdx = optionNames.indexOf(changedOptionName);
        if (changedIdx === -1) return;

        this.#tkFixingFlag = true;

        const optKeys = ['option1', 'option2', 'option3'];
        const variants = this.#tkProduct.variants || [];
        // Prefer available, fall back to first existing
        const bestVariant =
          variants.find(
            (/** @type {any} */ v) => v.available && v[optKeys[changedIdx]] === changedValue
          ) ||
          variants.find((/** @type {any} */ v) => v[optKeys[changedIdx]] === changedValue);

        if (bestVariant) {
          const newOpts = [bestVariant.option1, bestVariant.option2, bestVariant.option3];
          optionNames.forEach((/** @type {string} */ optName, /** @type {number} */ i) => {
            if (i <= changedIdx) return;
            const desired = newOpts[i];
            if (desired == null) return;
            // Pokus 1: radio fieldset
            const fs2 = this.querySelector(
              `fieldset[data-option-name="${optName.replace(/"/g, '\\"')}"]`
            );
            if (fs2) {
              const radios = fs2.querySelectorAll('input[type="radio"]');
              radios.forEach((r) => {
                /** @type {HTMLInputElement} */ (r).checked = false;
              });
              for (const r of /** @type {NodeListOf<HTMLInputElement>} */ (radios)) {
                if (r.value === desired) {
                  r.checked = true;
                  break;
                }
              }
              return;
            }
            // Pokus 2: select dropdown
            const sel2 = /** @type {HTMLSelectElement|null} */ (
              this.querySelector(`select[name="options[${optName}]"]`)
            );
            if (sel2) {
              for (let k = 0; k < sel2.options.length; k++) {
                if (sel2.options[k].value === desired) {
                  sel2.options[k].selected = true;
                  sel2.selectedIndex = k;
                } else {
                  sel2.options[k].selected = false;
                }
              }
            }
          });
        }

        setTimeout(() => {
          this.#tkFixingFlag = false;
        }, 500);
      },
      true // capture
    );
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
            fieldset.style.setProperty(
              '--pill-width-current',
              `${radios[newCurrentIndex].parentElement?.offsetWidth || 0}px`
            );
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
            fieldset.style.setProperty(
              '--pill-width-previous',
              `${radios[newPreviousIndex].parentElement?.offsetWidth || 0}px`
            );
          }
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }

    // OPTIMIZATION: Use section_id to return only the product section HTML
    // (not full page with header/footer) — much smaller response, faster parse+morph.
    // Skip when loadsNewProduct (combined listing URL change to different product) —
    // then updateMain() needs full <main> HTML.
    const isOnProductPage = this.dataset.templateProductMatch === 'true';
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = !!newUrl && newUrl !== currentUrl;
    if (isOnProductPage && !source && !loadsNewProduct) {
      const section = this.closest('[id^="shopify-section-"]');
      if (section) {
        const sectionId = section.id.replace('shopify-section-', '');
        if (productUrl?.includes('?')) productUrl = productUrl.split('?')[0];
        return `${productUrl}?section_id=${sectionId}&${params.join('&')}`;
      }
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    // `option:checked` reflects the actual `selected` property, not the HTML
    // attribute (which can be stale after morph reorders/renames option elements
    // — see morph.js positional matching on keyless OPTION nodes).
    const selectedOption = this.querySelector('select option:checked, fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    // `option:checked` reflects the actual `selected` property (one per single
    // select). The HTML `[selected]` attribute can leak stale selections after
    // morph re-renames keyless OPTION elements positionally — see morph.js
    // updateAttribute() boolean-attr handling.
    const selectedOptions = Array.from(this.querySelectorAll('select option:checked, fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
